import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Gamepad2, Trophy, Brain, Users, ExternalLink, ArrowRight, Sparkles } from 'lucide-react';

const Games = () => {
  const games = [
    {
      title: 'Wealthy Canadian Interactive Game',
      description: 'An innovative, hands-on guide to building wealth in Canada. Packed with quizzes, calculators, and personalized action plans.',
      features: ['Interactive quizzes', 'Financial calculators', 'Personalized action plans', 'Real-world scenarios'],
      image: 'https://images.unsplash.com/photo-1611996575749-79a3a250f948?w=600&q=80',
      link: 'https://harryvadalkar.ca/wealthy-canadian',
      color: 'amber',
    },
    {
      title: 'Money Smart Kids',
      description: 'An interactive online book/game designed for curious minds aged 6 to 13. Teaching essential financial skills through engaging games, stories, and real-world scenarios.',
      features: ['Ages 6-13', 'Engaging stories', 'Financial literacy games', 'Parent & teacher friendly'],
      image: 'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=600&q=80',
      link: 'https://harryvadalkar.ca/money-smart-kids',
      color: 'blue',
    },
  ];

  const skills = [
    { icon: Brain, title: 'Critical Thinking', description: 'Develop analytical skills for financial decisions' },
    { icon: Trophy, title: 'Goal Setting', description: 'Learn to set and achieve financial goals' },
    { icon: Users, title: 'Real-World Application', description: 'Apply concepts to actual life situations' },
  ];

  return (
    <div className="min-h-screen" data-testid="games-page">
      <Helmet>
        <title>Financial Literacy Games | Money Smart Kids | Wealthy Canadian Interactive Game | Learn Investing | Harry Vadalkar</title>
        <meta name="description" content="Interactive financial literacy games for all ages by Harry Vadalkar. Wealthy Canadian interactive wealth-building game with quizzes, calculators, and personalized action plans. Money Smart Kids financial education for children ages 6-13 with engaging stories and real-world scenarios. Learn investing through play." />
        <meta name="keywords" content="financial literacy games, money games for kids, investing games online, Wealthy Canadian interactive game, Money Smart Kids game, financial education games free, learn investing game, kids money education, financial literacy for children Canada, interactive money learning, financial games for teens, investment simulation, wealth building game, personal finance game, money management game kids, educational finance games, Canadian financial literacy, teaching kids about money, financial planning game, stock market game kids, budgeting game children" />
        <link rel="canonical" href="https://harryvadalkar.ca/games" />
      </Helmet>
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-green-900/20 to-[#020617]" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-green-500/20 text-green-400 px-4 py-2 rounded-full mb-6">
            <Gamepad2 className="w-4 h-4" />
            <span className="font-medium">Learn Through Play</span>
          </div>
          <h1 className="font-heading text-5xl md:text-6xl font-bold text-white leading-tight mb-6">
            Interactive <span className="text-green-400">Financial Games</span>
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Master financial concepts through engaging, interactive experiences. Perfect for all ages—from kids to adults building wealth.
          </p>
        </div>
      </section>

      {/* Games Section */}
      <section className="py-20" data-testid="games-list">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-12">
            {games.map((game, index) => (
              <div
                key={index}
                className="glass rounded-2xl overflow-hidden hover:border-slate-700 transition-all duration-300"
              >
                <div className={`grid lg:grid-cols-2 ${index % 2 === 1 ? 'lg:grid-flow-dense' : ''}`}>
                  <div className={`${index % 2 === 1 ? 'lg:col-start-2' : ''}`}>
                    <img
                      src={game.image}
                      alt={game.title}
                      className="w-full h-64 lg:h-full object-cover"
                    />
                  </div>
                  <div className="p-8 lg:p-12 flex flex-col justify-center">
                    <div className={`inline-flex items-center gap-2 ${game.color === 'amber' ? 'bg-amber-500/20 text-amber-400' : 'bg-blue-500/20 text-blue-400'} px-4 py-2 rounded-full mb-4 w-fit`}>
                      <Sparkles className="w-4 h-4" />
                      <span className="font-medium">Interactive Game</span>
                    </div>
                    <h2 className="font-heading text-3xl font-bold text-white mb-4">{game.title}</h2>
                    <p className="text-slate-300 text-lg mb-6">{game.description}</p>
                    
                    <ul className="grid grid-cols-2 gap-3 mb-8">
                      {game.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${game.color === 'amber' ? 'bg-amber-400' : 'bg-blue-400'}`} />
                          <span className="text-slate-400">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <a
                      href={game.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`game-link-${index}`}
                      className={`inline-flex items-center w-fit font-semibold px-6 py-3 rounded-xl transition-all ${
                        game.color === 'amber'
                          ? 'bg-amber-500 hover:bg-amber-600 text-black'
                          : 'bg-blue-600 hover:bg-blue-700 text-white'
                      }`}
                    >
                      Play Now
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Skills Section */}
      <section className="py-20 bg-slate-900/30" data-testid="skills-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl font-bold text-white mb-4">
              Skills You'll <span className="text-green-400">Develop</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {skills.map((skill, index) => (
              <div
                key={index}
                className="glass rounded-xl p-8 text-center hover:border-green-500/30 transition-all"
              >
                <skill.icon className="w-12 h-12 text-green-400 mx-auto mb-4" />
                <h3 className="font-semibold text-xl text-white mb-2">{skill.title}</h3>
                <p className="text-slate-400">{skill.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Games;